import { PDFName, PDFString, PDFHexString, PDFArray, PDFNumber, PDFRef } from 'pdf-lib';
import { buildTextAppearance, buildEditTextAppearance, resolveAppearanceFont, buildMarkupAppearance, buildInkAppearance, buildShapeAppearance, buildImageAppearance } from './appearanceStream';

const DEFAULT_COLOR = [0, 0, 0];

function hexToRgb01(hex) {
  if (!hex || typeof hex !== 'string') return DEFAULT_COLOR;
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return DEFAULT_COLOR;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  return [r, g, b];
}

/**
 * Convert Scrivar top-left page coords (origin at top) to PDF bottom-up coords.
 * width/height unchanged, y is flipped: pdfY = pageHeight - topY - boxHeight
 */
function rectFromTopLeft(x, y, w, h, pageHeight) {
  const nx = Math.min(x, x + (w || 0));
  const ny = y;
  const nw = Math.abs(w || 0);
  const nh = Math.abs(h || 0);
  const pdfY = pageHeight - ny - nh;
  return [nx, pdfY, nx + nw, pdfY + nh];
}

function makeBaseDict(ctx, { subtype, rect, color, opacity, scrivarId, contents }) {
  const entries = {
    Type: PDFName.of('Annot'),
    Subtype: PDFName.of(subtype),
    Rect: ctx.obj(rect.map((n) => PDFNumber.of(n))),
    F: PDFNumber.of(4), // PrintFlag
    NM: PDFString.of(`scrivar:${scrivarId}`),
    T: PDFString.of('Scrivar'),
  };
  if (color) entries.C = ctx.obj(color.map((n) => PDFNumber.of(n)));
  if (opacity !== undefined && opacity !== null) entries.CA = PDFNumber.of(opacity);
  if (contents !== undefined) entries.Contents = PDFHexString.fromText(contents);
  return ctx.obj(entries);
}

function attachAnnot(doc, page, dict) {
  const ref = doc.context.register(dict);
  let annots = page.node.get(PDFName.of('Annots'));
  if (!annots) {
    annots = doc.context.obj([]);
    page.node.set(PDFName.of('Annots'), annots);
  }
  const arr = annots instanceof PDFArray ? annots : doc.context.lookup(annots);
  arr.push(ref);
}

// ---------- Builders per type ----------

async function buildFreeText(doc, page, pageHeight, edit, fontCache) {
  const rect = rectFromTopLeft(edit.x, edit.y, edit.width, edit.height, pageHeight);
  const fontSize = Number(edit.styles?.fontSize) || 14;
  const color = hexToRgb01(edit.styles?.color || '#000000');
  const text = edit.content || (edit.segments || []).map((s) => s.text).join('') || '';
  const width = Math.abs(edit.width || 0);
  const height = Math.abs(edit.height || 0);
  const dict = makeBaseDict(doc.context, {
    subtype: 'FreeText',
    rect,
    color,
    opacity: 1,
    scrivarId: edit.id,
    contents: text,
  });
  // Build an /AP /N appearance stream so /AP-honoring viewers (Preview,
  // Acrobat, Chrome) paint OUR rendering verbatim instead of auto-redrawing
  // from /DA + /Contents — the latter double-draws with a ~1px offset.
  const font = await resolveAppearanceFont(doc, text, fontCache);
  const { xobjectRef, fontResourceName, height: appearanceHeight } = (edit.segments || edit.type === 'text') ? await buildEditTextAppearance(doc, edit, fontCache) : buildTextAppearance(doc, {
    text, font, fontSize, color, width, height, align: 'left',
  });
  if (appearanceHeight > height) dict.set(PDFName.of('Rect'), doc.context.obj(rectFromTopLeft(edit.x, edit.y, width, appearanceHeight, pageHeight)));
  const da = `/${fontResourceName} ${fontSize} Tf ${color[0]} ${color[1]} ${color[2]} rg`;
  dict.set(PDFName.of('DA'), PDFString.of(da));
  dict.set(PDFName.of('Q'), PDFNumber.of(0)); // left-align
  dict.set(PDFName.of('AP'), doc.context.obj({ N: xobjectRef }));
  attachAnnot(doc, page, dict);
}

// Embedded image data URLs above this many base64 chars stay on the baked +
// locked path (the sidecar strips their content for large docs; an /AP whose
// data the sidecar can't restore would vanish on reopen). ~1.5 MB of bytes.
const IMAGE_AP_MAX_CHARS = 2_000_000;

async function buildStamp(doc, page, pageHeight, edit) {
  const rect = rectFromTopLeft(edit.x, edit.y, edit.width, edit.height, pageHeight);
  const dict = makeBaseDict(doc.context, {
    subtype: 'Stamp',
    rect,
    color: hexToRgb01('#000000'),
    opacity: 1,
    scrivarId: edit.id,
    contents: edit.type === 'signature' ? 'Signature' : 'Image',
  });
  dict.set(PDFName.of('Name'), PDFName.of('Scrivar.Stamp'));
  const content = edit.content;
  if (content && content.length <= IMAGE_AP_MAX_CHARS) {
    try {
      const bytes = Uint8Array.from(atob(content.split(',')[1]), (c) => c.charCodeAt(0));
      const image = content.includes('image/png') ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
      const apRef = buildImageAppearance(doc, { image, boxW: Math.abs(edit.width || 0), boxH: Math.abs(edit.height || 0) });
      dict.set(PDFName.of('AP'), doc.context.obj({ N: apRef }));
    } catch { /* fall through: placeholder stamp, the bake covers it */ }
  }
  attachAnnot(doc, page, dict);
}

function buildInk(doc, page, pageHeight, annotation) {
  if (!annotation.points || annotation.points.length < 2) return;
  const xs = annotation.points.map((p) => p.x);
  const ys = annotation.points.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const rect = rectFromTopLeft(minX, minY, maxX - minX, maxY - minY, pageHeight);
  const flat = [];
  for (const p of annotation.points) {
    flat.push(p.x);
    flat.push(pageHeight - p.y);
  }
  const inkList = doc.context.obj([doc.context.obj(flat.map((n) => PDFNumber.of(n)))]);
  const dict = makeBaseDict(doc.context, {
    subtype: 'Ink',
    rect,
    color: hexToRgb01(annotation.color),
    opacity: annotation.opacity ?? 1,
    scrivarId: annotation.id,
  });
  dict.set(PDFName.of('InkList'), inkList);
  dict.set(PDFName.of('BS'), doc.context.obj({ W: PDFNumber.of(annotation.strokeWidth || 2) }));
  const apRef = buildInkAppearance(doc, {
    points: annotation.points, minX, minY, maxX, maxY,
    color: hexToRgb01(annotation.color), opacity: annotation.opacity ?? 1,
    strokeWidth: annotation.strokeWidth || 2,
  });
  dict.set(PDFName.of('AP'), doc.context.obj({ N: apRef }));
  attachAnnot(doc, page, dict);
}

function buildShape(doc, page, pageHeight, annotation, subtype) {
  const rect = rectFromTopLeft(annotation.x, annotation.y, annotation.w, annotation.h, pageHeight);
  const dict = makeBaseDict(doc.context, {
    subtype,
    rect,
    color: hexToRgb01(annotation.color),
    opacity: annotation.opacity ?? 1,
    scrivarId: annotation.id,
  });
  dict.set(PDFName.of('BS'), doc.context.obj({ W: PDFNumber.of(annotation.strokeWidth || 2) }));
  const sw0 = Math.abs(annotation.w || 0), sh0 = Math.abs(annotation.h || 0);
  const apRef = buildShapeAppearance(doc, {
    type: annotation.type, width: sw0, height: sh0,
    color: hexToRgb01(annotation.color), opacity: annotation.opacity ?? 1,
    strokeWidth: annotation.strokeWidth || 2,
  });
  dict.set(PDFName.of('AP'), doc.context.obj({ N: apRef }));
  attachAnnot(doc, page, dict);
}

function buildLine(doc, page, pageHeight, annotation) {
  const rect = [
    Math.min(annotation.x1, annotation.x2),
    pageHeight - Math.max(annotation.y1, annotation.y2),
    Math.max(annotation.x1, annotation.x2),
    pageHeight - Math.min(annotation.y1, annotation.y2),
  ];
  const dict = makeBaseDict(doc.context, {
    subtype: 'Line',
    rect,
    color: hexToRgb01(annotation.color),
    opacity: annotation.opacity ?? 1,
    scrivarId: annotation.id,
  });
  dict.set(
    PDFName.of('L'),
    doc.context.obj([
      PDFNumber.of(annotation.x1),
      PDFNumber.of(pageHeight - annotation.y1),
      PDFNumber.of(annotation.x2),
      PDFNumber.of(pageHeight - annotation.y2),
    ]),
  );
  if (annotation.type === 'arrow') {
    dict.set(
      PDFName.of('LE'),
      doc.context.obj([PDFName.of('None'), PDFName.of('ClosedArrow')]),
    );
  }
  dict.set(PDFName.of('BS'), doc.context.obj({ W: PDFNumber.of(annotation.strokeWidth || 2) }));
  const bw = rect[2] - rect[0], bh = rect[3] - rect[1];
  // page bottom-up endpoint = (x, pageHeight - y); box-local = subtract rect origin
  const lx1 = annotation.x1 - rect[0], ly1 = (pageHeight - annotation.y1) - rect[1];
  const lx2 = annotation.x2 - rect[0], ly2 = (pageHeight - annotation.y2) - rect[1];
  const apRef = buildShapeAppearance(doc, {
    type: annotation.type, width: bw, height: bh, lx1, ly1, lx2, ly2,
    color: hexToRgb01(annotation.color), opacity: annotation.opacity ?? 1,
    strokeWidth: annotation.strokeWidth || 2,
  });
  dict.set(PDFName.of('AP'), doc.context.obj({ N: apRef }));
  attachAnnot(doc, page, dict);
}

function buildMarkup(doc, page, pageHeight, annotation, subtype) {
  const rect = rectFromTopLeft(annotation.x, annotation.y, annotation.w, annotation.h, pageHeight);
  const [x1, y1, x2, y2] = rect;
  // QuadPoints defines the quadrilateral for the markup: (ul, ur, ll, lr) per spec 12.5.6.10
  const quadPoints = doc.context.obj([
    PDFNumber.of(x1), PDFNumber.of(y2),
    PDFNumber.of(x2), PDFNumber.of(y2),
    PDFNumber.of(x1), PDFNumber.of(y1),
    PDFNumber.of(x2), PDFNumber.of(y1),
  ]);
  const dict = makeBaseDict(doc.context, {
    subtype,
    rect,
    color: hexToRgb01(annotation.color),
    opacity: annotation.opacity ?? 1,
    scrivarId: annotation.id,
  });
  dict.set(PDFName.of('QuadPoints'), quadPoints);
  const [mw, mh] = [Math.abs(annotation.w || 0), Math.abs(annotation.h || 0)];
  const apRef = buildMarkupAppearance(doc, {
    type: annotation.type, width: mw, height: mh,
    color: hexToRgb01(annotation.color), opacity: annotation.opacity ?? 1,
  });
  dict.set(PDFName.of('AP'), doc.context.obj({ N: apRef }));
  attachAnnot(doc, page, dict);
}

// ---------- Dispatcher ----------

/**
 * Remove any /Annot dicts that a prior Scrivar save left behind. Without this
 * the Annots array grows on every save — each save adds a new FreeText/Stamp
 * for every edit, but nothing ever strips the previous save's entries. PDF.js
 * renders all of them, so old positions of a moved overlay "ghost through"
 * even when the content stream has been whited out at those positions.
 *
 * We only touch dicts we know we wrote (NM starting with "scrivar:" or the
 * reserved "scrivar-ff:" prefix). Third-party annotations (Acrobat, Preview)
 * are untouched so external edits stay round-trippable.
 */
export function purgeScrivarAnnots(doc) {
  const removed = [];
  for (const page of doc.getPages()) {
    const annotsRef = page.node.get(PDFName.of('Annots'));
    if (!annotsRef) continue;
    const arr = annotsRef instanceof PDFArray ? annotsRef : doc.context.lookup(annotsRef);
    if (!arr || typeof arr.remove !== 'function') continue;

    for (let i = arr.size() - 1; i >= 0; i--) {
      const ref = arr.get(i);
      const dict = doc.context.lookup(ref);
      if (!dict || typeof dict.get !== 'function') continue;
      const nm = dict.get(PDFName.of('NM'));
      if (!nm) continue;
      let name = '';
      try {
        name = typeof nm.decodeText === 'function' ? nm.decodeText() : nm.toString();
      } catch (_) {}
      if (typeof name === 'string' && (name.startsWith('scrivar:') || name.startsWith('scrivar-ff:'))) {
        let hadAp = false;
        // Before removing the array entry, free the detached indirect objects so
        // they aren't re-serialized on every save (pdf-lib doesn't GC them).
        // Without this, each save→reload→save cycle of a filled form grows the
        // file by ~1 FreeText dict + 1 /AP /N Form XObject.
        try {
          const ap = dict.get(PDFName.of('AP'));
          if (ap) {
            hadAp = true;
            const apDict = ap instanceof PDFRef ? doc.context.lookup(ap) : ap;
            const n = apDict && typeof apDict.get === 'function' ? apDict.get(PDFName.of('N')) : null;
            if (n instanceof PDFRef) doc.context.delete(n);
            if (ap instanceof PDFRef) doc.context.delete(ap);
          }
        } catch (_) {}
        if (ref instanceof PDFRef) { try { doc.context.delete(ref); } catch (_) {} }
        arr.remove(i);
        removed.push({ nm: name, hadAp });
      }
    }
  }
  return removed;
}

/**
 * Return a copy of the PDF with Scrivar's OWN annotations (NM scrivar:/scrivar-ff:)
 * removed, leaving all third-party annotations intact.
 *
 * Used on reopen of a file WE saved: PDF.js paints the returned (stripped) bytes
 * so the canvas does NOT contain our /FreeText /AP stamps, and the sidecar's
 * edits/annotations are restored into the editable layer instead. Net: canvas
 * stays faithful for third-party content while OUR content becomes editable —
 * with no double render (our annots live in exactly one place, the EditLayer).
 *
 * `apCarriedIds` reports which overlay ids were carried by a real /AP annot in
 * the opened bytes. Those overlays have NO pixels baked in the content stream
 * (the /AP was their only rendering, and it was just stripped) — so restore
 * must not fabricate `bakedBounds` for them, or MaskLayer/the save whiteout
 * paint white over raw page pixels (glaring on scans). A scrivar: annot
 * WITHOUT /AP is the >2MB-image placeholder — its pixels ARE baked, so its id
 * is deliberately excluded.
 *
 * @param {ArrayBuffer} arrayBuffer
 * @returns {Promise<{ bytes: Uint8Array, apCarriedIds: Set<string> }>}
 */
export async function stripScrivarAnnotsToBytes(arrayBuffer) {
  const { PDFDocument } = await import('pdf-lib');
  const doc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
  const removed = purgeScrivarAnnots(doc); // removes NM scrivar:/scrivar-ff: annots + GCs them
  const apCarriedIds = new Set();
  for (const { nm, hadAp } of removed) {
    if (hadAp && nm.startsWith('scrivar:')) apCarriedIds.add(nm.slice('scrivar:'.length));
  }
  const bytes = await doc.save({ useObjectStreams: true });
  return { bytes, apCarriedIds };
}

/**
 * Write standard PDF /Annot dictionaries for every Scrivar edit + annotation.
 * Mutates `doc` in place. Safe to call after the visual flatten pass — annotations
 * are metadata, they don't conflict with already-drawn content.
 *
 * Types without a clean PDF equivalent (checkmark, cross) are skipped — they
 * still round-trip via the ScrivarOverlays JSON sidecar.
 */
export async function writeStandardAnnotations(doc, edits, annotations) {
  // Strip any /Annots we wrote on prior saves before re-adding. Without this
  // every save multiplies the annotation count, and PDF.js paints each one —
  // a moved overlay looks duplicated at its old position on reload.
  purgeScrivarAnnots(doc);

  const pages = doc.getPages();
  // A single embedded Helvetica reused across every FreeText /AP appearance —
  // resolveAppearanceFont only swaps to Noto for non-WinAnsi text.
  const { StandardFonts } = await import('pdf-lib');
  const fontCache = { helv: await doc.embedFont(StandardFonts.Helvetica) };

  for (const edit of edits || []) {
    const page = pages[edit.pageNum - 1];
    if (!page) continue;
    if (edit.deleted) continue;
    if (edit.source === 'detected' && !edit.modified) continue;
    const pageHeight = page.getSize().height;
    try {
      if (edit.type === 'text' && (edit.content || edit.segments)) {
        await buildFreeText(doc, page, pageHeight, edit, fontCache);
      } else if ((edit.type === 'image' || edit.type === 'signature') && edit.content) {
        await buildStamp(doc, page, pageHeight, edit);
      }
    } catch (err) {
      throw new Error(`Could not render edit ${edit.id}: ${err.message}`, { cause: err });
    }
  }

  for (const annotation of annotations || []) {
    if (annotation.deleted) continue;
    const page = pages[annotation.pageNum - 1];
    if (!page) continue;
    const pageHeight = page.getSize().height;
    try {
      switch (annotation.type) {
        case 'draw':
          buildInk(doc, page, pageHeight, annotation);
          break;
        case 'rectangle':
          buildShape(doc, page, pageHeight, annotation, 'Square');
          break;
        case 'ellipse':
          buildShape(doc, page, pageHeight, annotation, 'Circle');
          break;
        case 'line':
        case 'arrow':
          buildLine(doc, page, pageHeight, annotation);
          break;
        case 'highlight':
          buildMarkup(doc, page, pageHeight, annotation, 'Highlight');
          break;
        case 'underline':
          buildMarkup(doc, page, pageHeight, annotation, 'Underline');
          break;
        case 'strikethrough':
          buildMarkup(doc, page, pageHeight, annotation, 'StrikeOut');
          break;
        case 'text':
          // Annotation-layer text note — FreeText with default font
          await buildFreeText(doc, page, pageHeight, {
            ...annotation,
            width: (annotation.text?.length || 8) * (annotation.fontSize || 14) * 0.6 + 8,
            height: (annotation.fontSize || 14) * 1.4 + 4,
            content: annotation.text || '',
            styles: { fontSize: annotation.fontSize || 14, color: annotation.color || '#000' },
          }, fontCache);
          break;
        // checkmark/cross have no native PDF subtype → generic /Stamp whose /AP
        // carries the strokes (buildShapeAppearance handles the geometry).
        case 'checkmark':
        case 'cross':
          buildShape(doc, page, pageHeight, annotation, 'Stamp');
          break;
        default:
          break;
      }
    } catch (err) {
      throw new Error(`Could not render annotation ${annotation.id}: ${err.message}`, { cause: err });
    }
  }
}

/**
 * Read standard PDF /Annot dictionaries from a PDF.js document and convert
 * them into Scrivar overlay shape. Used as a fallback when no Scrivar sidecar
 * is present (file came from Acrobat, Preview, etc.).
 *
 * Skips annotations named scrivar:* — those are written by our own
 * writeStandardAnnotations and will be restored from the sidecar JSON instead.
 *
 * @param {import('pdfjs-dist').PDFDocumentProxy} pdfjsDoc
 * @returns {Promise<{ edits: Array, annotations: Array }>}
 */
export async function readStandardAnnotationsAsOverlays(pdfjsDoc) {
  const edits = [];
  const annotations = [];
  let idCounter = 0;
  const nextId = () => `imported-${Date.now()}-${idCounter++}`;

  for (let p = 1; p <= pdfjsDoc.numPages; p++) {
    const page = await pdfjsDoc.getPage(p);
    const height = page.getViewport ? page.getViewport({ scale: 1 })?.height : 792;
    const annots = await page.getAnnotations();
    for (const a of annots) {
      const name = a.name || a.NM || '';
      if (typeof name === 'string' && name.startsWith('scrivar:')) continue;
      const [x1, y1, x2, y2] = a.rect || [0, 0, 0, 0];
      const topLeftX = Math.min(x1, x2);
      const topLeftY = height - Math.max(y1, y2);
      const w = Math.abs(x2 - x1);
      const h = Math.abs(y2 - y1);
      const color = a.color ? rgbToHex(a.color) : '#000000';
      const opacity = a.opacity ?? 1;

      switch (a.subtype) {
        case 'FreeText':
          edits.push({
            id: nextId(),
            pageNum: p,
            type: 'text',
            x: topLeftX,
            y: topLeftY,
            width: w,
            height: h,
            content: a.contentsObj?.str || a.contents || '',
            styles: { fontSize: 14, color },
          });
          break;
        case 'Square':
          annotations.push({ id: nextId(), pageNum: p, type: 'rectangle', x: topLeftX, y: topLeftY, w, h, color, strokeWidth: 2, opacity });
          break;
        case 'Circle':
          annotations.push({ id: nextId(), pageNum: p, type: 'ellipse', x: topLeftX, y: topLeftY, w, h, color, strokeWidth: 2, opacity });
          break;
        case 'Line':
          annotations.push({ id: nextId(), pageNum: p, type: 'line', x1: x1, y1: height - y1, x2: x2, y2: height - y2, color, strokeWidth: 2, opacity });
          break;
        case 'Highlight':
          annotations.push({ id: nextId(), pageNum: p, type: 'highlight', x: topLeftX, y: topLeftY, w, h, color, opacity });
          break;
        case 'Underline':
          annotations.push({ id: nextId(), pageNum: p, type: 'underline', x: topLeftX, y: topLeftY, w, h, color, opacity });
          break;
        case 'StrikeOut':
          annotations.push({ id: nextId(), pageNum: p, type: 'strikethrough', x: topLeftX, y: topLeftY, w, h, color, opacity });
          break;
        case 'Ink':
          if (a.inkLists && a.inkLists.length > 0) {
            const points = a.inkLists[0].map((pt) => ({ x: pt.x, y: height - pt.y }));
            annotations.push({ id: nextId(), pageNum: p, type: 'draw', points, color, strokeWidth: 2, opacity });
          }
          break;
        case 'Stamp':
          // No image content recoverable without rasterizing the appearance
          // stream — skip for V1. Scrivar-generated stamps restore from sidecar.
          break;
        default:
          break;
      }
    }
  }
  return { edits, annotations };
}

function rgbToHex(rgb) {
  if (!Array.isArray(rgb) || rgb.length < 3) return '#000000';
  const to255 = (v) => Math.max(0, Math.min(255, Math.round((v > 1 ? v : v * 255))));
  const hex = (n) => to255(n).toString(16).padStart(2, '0');
  return `#${hex(rgb[0])}${hex(rgb[1])}${hex(rgb[2])}`;
}
